import { motion } from "framer-motion";

export default function TheLaw() {
    return (
        <section
            id="thelaw"
            data-testid="the-law-section"
            className="relative w-full bg-[color:var(--paper)] text-neutral-900 py-24 sm:py-32 border-t border-neutral-300"
        >
            <div className="max-w-[92rem] mx-auto px-6 sm:px-12 lg:px-16 grid grid-cols-12 gap-6 sm:gap-8">
                {/* Left — numeral column */}
                <div className="col-span-12 sm:col-span-3">
                    <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-4">
                        Chapter VII — The Law
                    </div>
                    <div className="font-sans tabular text-7xl sm:text-8xl font-light leading-none text-neutral-900">
                        07
                    </div>
                    <div className="mt-4 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500 leading-relaxed">
                        New York EPTL § 5-4.3<br />Enacted 1847
                    </div>
                </div>

                {/* Right — text column */}
                <div className="col-span-12 sm:col-span-9 space-y-8">
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight text-neutral-950 max-w-4xl"
                    >
                        Why This Law{" "}
                        <span className="italic font-light text-neutral-500">
                            Protects Hospitals.
                        </span>
                    </motion.h2>

                    <div className="font-serif text-lg sm:text-xl leading-relaxed text-neutral-800 max-w-3xl space-y-5">
                        <p>
                            My daughter Danielle spent forty-one days in that
                            hospital. Thirty-two of them on a ventilator. She
                            did not come home.
                        </p>
                        <p>
                            Under a New York law written in 1847, here is what
                            her death is worth.
                        </p>
                    </div>

                    {/* Statute block */}
                    <div className="max-w-3xl">
                        <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
                            New York Estates, Powers & Trusts Law § 5-4.3(a)
                        </div>
                        <blockquote
                            data-testid="statute-block"
                            className="border border-neutral-400 bg-white/50 p-6 sm:p-8 font-mono text-[13px] sm:text-sm leading-relaxed text-neutral-900"
                        >
                            The damages awarded to the plaintiff may be such
                            sum as the jury deems to be fair and just
                            compensation for the{" "}
                            <span className="text-[#981c28] font-bold border-b-2 border-[#981c28]">
                                pecuniary injuries
                            </span>{" "}
                            resulting from the decedent&rsquo;s death to the
                            persons for whose benefit the action is brought…
                            the reasonable expenses of medical aid, nursing and
                            attention incident to the injury causing death and
                            the reasonable funeral expenses of the decedent…
                            shall also be proper elements of damage.
                        </blockquote>
                        <a
                            href="https://www.nysenate.gov/legislation/laws/EPT/5-4.3"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="statute-link"
                            className="mt-3 inline-block font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-700 hover:text-neutral-950 ink-link"
                        >
                            Read the full statute →
                        </a>
                    </div>

                    <div className="font-serif text-lg sm:text-xl leading-relaxed text-neutral-800 max-w-3xl space-y-5">
                        <p>
                            The statute allows a family to recover only
                            pecuniary injuries. Money. Nothing else. Not grief.
                            Not anguish. Not a mother watching her child suffer
                            for six weeks.
                        </p>
                        <p>
                            Danielle had no job, so there are no lost wages.
                            She had no children, so there is no lost parenting
                            to compensate. And New York compensates a child who
                            loses a parent, but never a parent who loses a
                            child. No wages. No dependents. No compensable
                            relationship to her own mother. Add the funeral.
                            That is the entire ledger.
                        </p>
                        <p>Now understand what that does.</p>
                    </div>

                    {/* Pull quote */}
                    <motion.blockquote
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-3xl border-l-2 border-[#981c28] pl-6 sm:pl-8 font-serif text-2xl sm:text-3xl lg:text-4xl leading-snug tracking-tight text-neutral-950"
                    >
                        If Danielle had survived those forty-one days alive but
                        destroyed, her suffering would have been fully
                        compensable.{" "}
                        <span className="italic font-light text-neutral-600">
                            She died instead — and the exposure went down.
                        </span>
                    </motion.blockquote>

                    <div className="font-serif text-lg sm:text-xl leading-relaxed text-neutral-800 max-w-3xl space-y-5">
                        <p>
                            The sponsors of the bill to repeal this rule said
                            it plainly in the official record: the law lets a
                            wrongdoer take advantage of a rule that makes it
                            cheaper to kill someone than to seriously injure
                            them.
                        </p>
                        <p>
                            That is why ninety attorneys could not take my
                            case. Not because there was no case. Because there
                            was no recovery — and no firm can front two hundred
                            thousand dollars against a column of zeros.
                        </p>
                        <p>
                            The law does not just fail families like mine. It
                            makes us the safest patients in the building to
                            lose.
                        </p>
                    </div>

                    {/* Subhead */}
                    <div className="max-w-3xl pt-6">
                        <div className="font-sans text-xs uppercase tracking-[0.35em] text-neutral-700 font-semibold pb-2 border-b border-neutral-400">
                            Four Vetoes
                        </div>
                    </div>

                    <div className="font-serif text-lg sm:text-xl leading-relaxed text-neutral-800 max-w-3xl space-y-5">
                        <p>
                            The Grieving Families Act would end this. It has
                            passed both houses of the New York Legislature four
                            times. Governor Hochul has vetoed it four times —
                            most recently on December 5, 2025 — citing
                            insurance premiums and the cost to hospitals.{" "}
                            <span className="italic">The same hospitals.</span>
                        </p>
                        <a
                            href="https://www.nysenate.gov/legislation/bills/2025/S4423"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="grieving-families-link"
                            className="inline-block font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-700 hover:text-neutral-950 ink-link"
                        >
                            Read the Grieving Families Act →
                        </a>
                    </div>

                    {/* Signature block */}
                    <div className="max-w-3xl pt-8 mt-4 border-t border-neutral-300 flex items-center gap-4">
                        <span className="w-12 h-px bg-neutral-500" />
                        <div>
                            <div className="font-serif italic text-lg sm:text-xl text-neutral-800">
                                In faith and fierce love,
                            </div>
                            <div className="mt-1 font-serif text-2xl sm:text-3xl text-neutral-950">
                                Rebecca
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
